import React, { useState, useEffect, useRef } from "react";
import moment from 'moment';
import styled from 'styled-components';
import { Modal, Button, FormField } from '@prosolve/common-components-ui'
import useForm from '../../../../hooks/useForm';
import SessionScheduleManager from "../../../SessionScheduleManager/SessionScheduleManager";
import useDataSessions from '../../../../dataHooks/useDataSessions';
import useDataSessionBlueprints from '../../../../dataHooks/useDataSessionBlueprints';
import useFetch from '../../../../hooks/useFetch';
import { formatDateSpan } from '../../../../utils';
import Cookies from 'js-cookie';

const HorizontalDivider = styled.div`
  display: flex;
  align-items: center;
  text-align: center;
  gap: 15px;
  color: #565656;
  margin: 10px 0px;

  &::before,
  &::after {
    content: "";
    flex: 1;
    border-bottom: 1px solid #C2C9D1;
  }
`;

export default ({
  initialSessions = [],
  eventId,
  defaultStartDate,
  onClose,
  onSuccess,
}) => {
  const fetch = useFetch()

  const userId = Cookies.get('loggedInUserId');

  const {
    fetchSessionBlueprints
  } = useDataSessionBlueprints();

  const formSchema = {
    requiredFields: ['date'],
    optionalFields: ['sessionBlueprintId', 'existingEventId'],
    initData: { date: (defaultStartDate || '') }
  };
  
  const formData = useForm(formSchema)

  console.log(formData.values)

  const {
    newSessions,
    deleteSessions
  } = useDataSessions();

  const [isBusy, setIsBusy] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isEventsListLoading, setIsEventsListLoading] = useState(false);
  const [filterOptionsVal, setFilterOptionsVal] = useState()
  const [sessionBlueprints, setSessionBlueprints] = useState([])
  const [filterOptionsElVal, setFilterOptionsElVal] = useState()
  const [eventsList, setEventsList] = useState([])
  const [sessions, setSessions] = useState([])
  const [isPreview, setIsPreview] = useState(false)

  /*
   * This method is used to process the sessions from our blueprint by applying the eventId as well as
   * transforming the base dates set on the sessions into the proper dates selected in this form
   */
  const processBlueprintSessions = (blueprintSessions) => {
    const uniqueDates = []
    blueprintSessions.forEach(({ date }) => {
      if (uniqueDates.indexOf(date) < 0) {
        uniqueDates.push(date);
      }
    })
    const sessionDays = uniqueDates
      .map((date) => moment(date, 'YYYY-MM-DD'))
      .sort((a, b) => a - b)

    const dayZero = sessionDays[0]
    
    const dayOffsetMap = {}
    sessionDays.forEach(day => {
      const dayStr = day.format('YYYY-MM-DD')
      const diffInDays = day.diff(dayZero, 'days');
      dayOffsetMap[dayStr] = diffInDays;
    });

    const selectedDayZero = formData.values.date;

    return blueprintSessions.map(bSession => {
      const { date } = bSession;
      const dayOffset = dayOffsetMap[date] || 0;
      const processedDate = moment(selectedDayZero,'YYYY-MM-DD').add(dayOffset, 'days').format('YYYY-MM-DD');

      const returnObj = {
        ...bSession,
        date: processedDate,
        eventId
      }

      if (returnObj._id) {
        delete returnObj._id
      }

      return returnObj
    })
  }

  const sessionScheduleManagerRef = useRef()

  const onClickBack = async () => {
    setSessions([])
    setIsPreview(false)
  }

  const fetchEventSessions = async (eventId) => {
    try {
      const response = await fetch(`events/event/sessions?eventId=${eventId}`, { method: 'GET' })
      const jsonData = await response.json()
      if (jsonData.error) {
        console.log(jsonData.error)
        return []
      } else {
        return jsonData
      }
    } catch (e) {
      console.log(e)
      return []
    }
  }

  const onClickNext = async () => {
    const { sessionBlueprintId, existingEventId } = formData.values;
    if (formData.isValid() && (sessionBlueprintId || existingEventId)) {
      if (sessionBlueprintId) {
        const sessionBlueprintObj = sessionBlueprints.find(({ _id }) => _id === sessionBlueprintId)
        if (sessionBlueprintObj) {
          const { sessions: blueprintSessions } = sessionBlueprintObj;
          setSessions(processBlueprintSessions(blueprintSessions))
          setIsPreview(true)
        }
      } else if (existingEventId) {
        const existingEventSessions = await fetchEventSessions(existingEventId)
        setSessions(processBlueprintSessions(existingEventSessions))
        setIsPreview(true)
      }
    }
  }

  const onClickSubmit = async () => {
    if (!isBusy) {
      setIsBusy(true)

      // Always delete current session before adding new ones in
      if (initialSessions.length > 0) {
        await deleteSessions(initialSessions)
      }

      if (sessions.length > 0) {
        await newSessions(sessions)
      }

      window.dispatchEvent(new CustomEvent('reloadSessions', {}));

      setIsBusy(false)
      onSuccess()
    }
  }

  const getSessionBlueprints = async () => {
    setIsLoading(true)
    try {
      const sBlueprints = await fetchSessionBlueprints();
      setSessionBlueprints(sBlueprints)
    } catch (e) {
      setSessionBlueprints([])
    }
    setIsLoading(false)
  }

  // TODO: add user id to url params below to restrict available events to copy from
  const fetchEventsList = async () => {
    try {
      setIsEventsListLoading(true);
      const response = await fetch(`events?userId=${userId}`, { method: 'GET' })
      const jsonData = await response.json()
      if (jsonData.error) {
        setEventsList([])
      } else {
        setEventsList(jsonData)
      }
     setIsEventsListLoading(false)
    } catch (e) {
      console.log(e)
      setEventsList([])
      setIsEventsListLoading(false)
    }
  }

  useEffect(() => {
    getSessionBlueprints()
    fetchEventsList()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])


  const sessionSelectorRef = useRef()
  const eventSelectorRef = useRef()

  const { sessionBlueprintId, existingEventId } = formData.values;

  useEffect(() => {
    if (sessionBlueprintId) {
      formData.setValues({ ...formData.values, existingEventId: '' })
      if (eventSelectorRef && eventSelectorRef.current && eventSelectorRef.current.clearInput) {
        eventSelectorRef.current.clearInput()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionBlueprintId])

  useEffect(() => {
    if (existingEventId) {
      formData.setValues({ ...formData.values, sessionBlueprintId: '' })
      if (sessionSelectorRef && sessionSelectorRef.current && sessionSelectorRef.current.clearInput) {
        sessionSelectorRef.current.clearInput()
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingEventId])

  const filteredSessionBlueprints = sessionBlueprints
  .reduce(function(filtered, sessionBlueprint) {
    const { _id: id = '', name = '' } = sessionBlueprint;

    let passesFilter;
    if (filterOptionsVal) {
      if (name.toLowerCase().includes(filterOptionsVal.toLowerCase())) {
        passesFilter = true;
      } else {
        passesFilter = false;
      }
    } else {
      passesFilter = true;
    }

    if (passesFilter) {
      filtered.push({ value : id, text: name, sortText: name });
    }

    return filtered;
  }, [])
  .sort(({ sortText: textA }, {sortText: textB }) => {
    return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
  })

  const filteredEventsList = eventsList
  .reduce(function(filtered, eventData) {
    const { id = '', name = '', startDate = '', endDate = '' } = eventData;

    const displayName = `${name} (${formatDateSpan(startDate, endDate)})`

    let passesFilter;
    if (filterOptionsElVal) {
      if (displayName.toLowerCase().includes(filterOptionsElVal.toLowerCase())) {
        passesFilter = true;
      } else {
        passesFilter = false;
      }
    } else {
      passesFilter = true;
    }

    if (passesFilter) {
      filtered.push({ value : id, text: displayName, sortText: displayName });
    }

    return filtered;
  }, [])
  .sort(({ sortText: textA }, {sortText: textB }) => {
    return (textA < textB) ? -1 : (textA > textB) ? 1 : 0;
  })

  const footer = (
    <div style={{ 
      display: 'flex',
      justifyContent: 'flex-end',
      alignItems: 'center',
      columnGap: '10px',
      padding: '10px'
    }}>
      <Button onClick={isPreview ? onClickBack : onClose}>
        {isPreview ? 'Back' : 'Cancel'}
      </Button>
      <Button onClick={isPreview ? onClickSubmit : onClickNext} $colored={true} $disabled={isBusy}>
        {isPreview ? 'Confirm' : 'Next'}
      </Button>
    </div>
  )

  return (
    <Modal title='Copy Schedule' isFullScreen={true} onClose={onClose} footer={footer}>
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        rowGap: '15px',
        height: '100%',
        position: 'relative'
      }}>
        {
          !isPreview ?
          <>
          <FormField
            fieldId='date'
            type='date'
            label='Date'
            placeholder=''
            formData={formData}
          />
          <HorizontalDivider style={{ marginTop: '20px', marginBottom: '20px'}}>Select Copy from Template Or Existing Event Below</HorizontalDivider>
          <FormField
            ref={sessionSelectorRef}
            fieldId='sessionBlueprintId'
            type='search'
            label='Copy from Event Template'
            formData={formData}
            options={filteredSessionBlueprints}
            onSearch={(val) => setFilterOptionsVal(val)}
            isLoading={isLoading}
            width='100%'
            maxHeight='300px'
          />
          <HorizontalDivider>Or</HorizontalDivider>
          <FormField
            ref={eventSelectorRef}
            fieldId='existingEventId'
            type='search'
            label='Copy from Existing Event'
            formData={formData}
            options={filteredEventsList}
            onSearch={(val) => setFilterOptionsElVal(val)}
            isLoading={isEventsListLoading}
            width='100%'
            maxHeight='300px'
          />
          </>
          :
          <SessionScheduleManager
            ref={sessionScheduleManagerRef}
            sessions={sessions}
            eventId={eventId}
            isReadOnly={true}
          />
        }
      </div>
    </Modal>
  )
}